import { Markdown } from "@tiptap/markdown";
import { EditorContent, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./TipTapEditor.css";
import { CitationMarker } from "../TimelineItem/components/CitationMarker";
import {
  citationGroups,
  type SummaryCitationAnchor,
} from "../TimelineItem/utils/citationMarkers";
import { FloatingToolbar } from "./components/FloatingToolbar";
import { StaticToolbar } from "./components/StaticToolbar";
import { SuggestionBubbleMenu } from "./components/SuggestionBubbleMenu";
import {
  CitationDecorationExtension,
  citationDecorationPluginKey,
} from "./extensions/CitationDecorationExtension";
import { DiffDeletionNode } from "./extensions/DiffDeletionNode";
import { DiffInsertionMark } from "./extensions/DiffInsertionMark";
import { createBaseExtensions } from "./extensions/markdownExtensions";
import {
  SearchHighlightExtension,
  searchHighlightPluginKey,
} from "./extensions/SearchHighlightExtension";
import { SuggestionHighlightMark } from "./extensions/SuggestionHighlightMark";
import type { DiffModeConfig, TextRangeSuggestion } from "./types";
import {
  applyDiffToEditor,
  clearDiffFromEditor,
  textPosToDocPos,
} from "./utils/diffUtils";
import {
  findSuggestionRange,
  isMatchUsable,
  ValidationState,
} from "./utils/suggestionMatcher";

export interface TipTapEditorProps {
  /**
   * Markdown content
   */
  content: string;

  /**
   * Callback when content changes (receives markdown)
   */
  onChange?: (markdown: string) => void;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Additional CSS class
   */
  className?: string;

  /**
   * Key to force remount.
   * Increment this when programmatically changing content (e.g., discard changes)
   */
  resetKey?: number;

  /**
   * Diff mode configuration. When enabled, shows inline diff visualization
   * comparing current content against originalContent.
   * Editor becomes read-only when diff mode is active.
   */
  diffMode?: DiffModeConfig;

  /**
   * AI-generated text range suggestions to highlight for potential deletion.
   * Each suggestion will be rendered as a highlighted span with a bubble menu
   * for accepting or rejecting.
   */
  suggestions?: TextRangeSuggestion[];

  /** Navigate to the retained source entry for a cross-entry suggestion. */
  onNavigateToSegment?: (segmentId: string) => void;

  /**
   * Callback when a text range suggestion is accepted (text will be deleted).
   * Called after the text is deleted from the editor.
   */
  onSuggestionAccept?: (suggestionId: string) => void;

  /**
   * Callback when a text range suggestion is rejected (dismissed).
   * Called after the highlight mark is removed.
   */
  onSuggestionReject?: (suggestionId: string) => void;

  /**
   * Controls toolbar rendering.
   * - "floating" (default): BubbleMenu toolbar on text selection
   * - "static": Always-visible toolbar above the editor
   * - "none": No toolbar
   */
  toolbarVariant?: "floating" | "static" | "none";

  /**
   * Search query to highlight in editor content (using ProseMirror decorations).
   * Empty string or undefined means no highlighting.
   */
  searchQuery?: string;

  /**
   * Index of the currently active search match within this editor (0-based).
   * -1 or undefined means no active match.
   */
  activeMatchIndex?: number;

  /**
   * Source citations anchored in this entry's summary, rendered as `[1][2]`
   * markers. Omitted (or empty) when the citation feature is off, in which case
   * the editor behaves exactly as it did before citations existed.
   */
  citations?: readonly SummaryCitationAnchor[];

  /** Jump the document viewer to a citation's source block. */
  onOpenCitation?: (citationRowId: string) => void;

  /** Soft-delete one citation link. */
  onRemoveCitation?: (citationRowId: string) => void;
}

/**
 * TipTap-based markdown editor for Timeline content.
 *
 * Features:
 * - Markdown serialization/deserialization
 * - Floating toolbar (BubbleMenu) on text selection
 * - Keyboard shortcuts (Cmd/Ctrl+B, I, U)
 * - Limited formatting: bold, italic, underline, strikethrough, lists, blockquotes
 * - No headings or code blocks
 * - Diff mode: inline diff visualization with read-only state
 */
export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  content,
  onChange,
  placeholder,
  className,
  resetKey = 0,
  diffMode,
  suggestions,
  onSuggestionAccept,
  onSuggestionReject,
  onNavigateToSegment,
  searchQuery,
  activeMatchIndex,
  citations,
  onOpenCitation,
  onRemoveCitation,
  toolbarVariant = "floating",
}) => {
  const isDiffMode = diffMode?.enabled ?? false;
  const originalContent = diffMode?.originalContent ?? "";
  const prevDiffModeRef = useRef(isDiffMode);
  const hasSuggestions = suggestions && suggestions.length > 0;

  // Track whether suggestions have been applied (apply once on initial render)
  const suggestionsAppliedRef = useRef(false);

  // Stores the normalized markdown baseline set by onCreate (editor ready).
  // Used to distinguish TipTap's initial normalization from actual user edits.
  const normalizedContentRef = useRef<string | null>(null);

  // Create extensions with placeholder and diff support
  const extensions = useMemo(
    () => [
      ...createBaseExtensions(placeholder),
      Markdown.configure({
        markedOptions: {
          gfm: true, // GitHub Flavored Markdown
          breaks: true, // Convert single newlines to <br>
        },
      }),
      // Always include diff extensions so commands are available
      DiffInsertionMark,
      DiffDeletionNode,
      // Always include suggestion highlight mark for AI suggestions
      SuggestionHighlightMark,
      // Search term highlighting via ProseMirror decorations (visual-only, no document changes)
      SearchHighlightExtension,
      // Citation markers, also decorations — they must never enter the document,
      // which is what a save writes back to the segment's text.
      CitationDecorationExtension,
    ],
    [placeholder],
  );

  // Memoize the update handler to prevent unnecessary re-renders
  const handleUpdate = useCallback(
    ({ editor }: { editor: ReturnType<typeof useEditor> }) => {
      // Don't call onChange in diff mode (read-only)
      if (editor && onChange && !isDiffMode) {
        // Skip updates before onCreate has established the normalized baseline.
        // onCreate marks the editor as ready — any onUpdate before that is not
        // a real user edit.
        if (normalizedContentRef.current === null) return;
        const mdContent = editor.getMarkdown();
        if (mdContent !== normalizedContentRef.current) {
          normalizedContentRef.current = mdContent;
          onChange(mdContent);
        }
      }
    },
    [onChange, isDiffMode],
  );

  const editor = useEditor(
    {
      extensions,
      content,
      contentType: "markdown", // Parse initial content as markdown
      onCreate: ({ editor }) => {
        // Capture TipTap's normalized markdown as the baseline.
        // Any whitespace normalization (e.g., blank line removal) is absorbed here
        // so that onUpdate won't treat it as a user edit.
        normalizedContentRef.current = editor.getMarkdown();
        // Fresh editor instance (initial mount or resetKey-triggered recreation) —
        // allow suggestion highlights to be re-applied to the new content.
        suggestionsAppliedRef.current = false;
      },
      onUpdate: handleUpdate,
      editable: !isDiffMode, // Read-only in diff mode
      editorProps: {
        attributes: {
          class: `indigo-tiptap-editor__content${isDiffMode ? " indigo-tiptap-editor__content--diff-mode" : ""}`,
        },
      },
    },
    // Re-create editor when resetKey or diff mode changes
    [resetKey, isDiffMode],
  );

  // NOTE: resetKey is a dependency of useEditor above, so bumping it fully
  // destroys and recreates the editor with the current `content` prop — the
  // new instance already reflects the reset content. We intentionally do NOT
  // manually setContent here: that effect would run against the *previous*
  // (already-destroyed) editor instance still captured in this render's
  // closure (useEditor's recreation only propagates the new instance on a
  // later render), throwing "Cannot read properties of null (reading
  // 'commands')" on reset / discard in production builds.

  // Apply or clear diff visualization when diff mode changes
  useEffect(() => {
    if (!editor) return;

    if (isDiffMode && originalContent) {
      // Apply diff visualization after a small delay to ensure content is set
      const timer = setTimeout(() => {
        applyDiffToEditor(editor, originalContent);
      }, 50);
      return () => clearTimeout(timer);
    } else if (!isDiffMode && prevDiffModeRef.current) {
      // Exiting diff mode - clear diff markers
      clearDiffFromEditor(editor);
    }

    prevDiffModeRef.current = isDiffMode;
  }, [editor, isDiffMode, originalContent]);

  // Apply suggestion highlights once when entering edit mode with suggestions.
  // Marks stay attached to their text via ProseMirror's transaction system.
  // We don't re-apply because offsets become stale after document modifications.
  useEffect(() => {
    if (!editor || !hasSuggestions || isDiffMode) return;
    if (suggestionsAppliedRef.current) return; // Already applied

    // Store current cursor position to restore after applying highlights
    const { from: cursorPos } = editor.state.selection;

    // Apply highlights once after a small delay to ensure editor content is ready
    const timer = setTimeout(() => {
      suggestions?.forEach((suggestion) => {
        const plainText = editor.state.doc.textContent;

        // Find the suggestion text with context verification
        // This handles markdown normalization, occurrence index, and fuzzy context matching
        const matchResult = findSuggestionRange(
          plainText,
          suggestion.textRange,
        );

        // Skip if text not found in document
        if (!isMatchUsable(matchResult)) return;

        // Convert plain text positions to ProseMirror document positions
        const from = textPosToDocPos(editor, matchResult.start!);
        const to = textPosToDocPos(editor, matchResult.end!);

        // Validate that text still exists at this position
        const docText = editor.state.doc.textBetween(from, to, "");
        const textMatches = docText === matchResult.normalizedText;

        // Consider valid if text matches AND we have high confidence (VALID state)
        // CONTEXT_MISMATCH and OCCURRENCE_SHIFTED are still usable but marked as lower confidence
        const isValid =
          textMatches && matchResult.state === ValidationState.VALID;

        editor.commands.setSuggestionHighlight(
          {
            suggestionId: suggestion.id,
            isValid,
            reason: suggestion.reason,
            reasonDetails: suggestion.reasonDetails ?? undefined,
            relatedSegmentID: suggestion.relatedSegmentID,
            confidence: suggestion.confidence ?? undefined,
            textRange: suggestion.textRange,
          },
          from,
          to,
        );
      });

      suggestionsAppliedRef.current = true;

      // Restore cursor position after applying all highlights
      if (cursorPos <= editor.state.doc.content.size) {
        editor.commands.setTextSelection(cursorPos);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [editor, suggestions, hasSuggestions, isDiffMode]);

  // Listen for custom focus-start event dispatched by keyboard navigation
  useEffect(() => {
    const element = editor?.view.dom;
    if (!element) return;

    const handler = () => {
      editor.commands.focus("start");
    };

    element.addEventListener("tiptap-focus-start", handler);
    return () => element.removeEventListener("tiptap-focus-start", handler);
  }, [editor]);

  // Markers are drawn by React into host nodes that ProseMirror places as
  // widget decorations. Keeping the nodes here rather than building DOM in the
  // extension is what lets display and edit mode share one `CitationMarker`,
  // menu and confirmation modal included.
  const citationHostsRef = useRef(new Map<string, HTMLElement>());
  const [citationHosts, setCitationHosts] = useState<
    { key: string; host: HTMLElement; ids: string[] }[]
  >([]);
  // Read back from the extension after it places the markers rather than
  // resolved a second time here: the numbers have to describe the markers that
  // actually landed, and they are frozen at placement so a claim keeps its
  // number while it is being edited.
  const [citationNumbers, setCitationNumbers] = useState<Map<string, number>>(
    new Map(),
  );

  const citationsRenderable =
    (citations?.length ?? 0) > 0 &&
    !!onOpenCitation &&
    !!onRemoveCitation &&
    !isDiffMode;

  useEffect(() => {
    if (!editor) return;
    if (!citationsRenderable) {
      setCitationHosts([]);
      setCitationNumbers(new Map());
      editor.view.dispatch(
        editor.state.tr.setMeta(citationDecorationPluginKey, { kind: "clear" }),
      );
      return;
    }

    const hosts = citationHostsRef.current;
    const hostFor = (key: string): HTMLElement => {
      const existing = hosts.get(key);
      if (existing) return existing;
      const host = document.createElement("span");
      host.className = "indigo-tiptap-editor__citation-host";
      // The marker is chrome, not content: without this the browser lets the
      // caret enter it and a select-all would carry the `[1]` into the
      // clipboard as though it were part of the summary.
      host.contentEditable = "false";
      hosts.set(key, host);
      return host;
    };

    // Placed once per editor; a later change to the list -- which `Remove`
    // itself causes, by refetching the rows -- is reconciled against the
    // markers already on screen rather than re-resolved, so a claim the
    // reviewer has edited keeps its marker.
    editor.view.dispatch(
      editor.state.tr.setMeta(citationDecorationPluginKey, {
        kind: "citations",
        citations: citations ?? [],
        hostFor,
      }),
    );

    // Portal into every group, anchored or not. An unanchored group's host is
    // simply never inserted into the editor, so the markers React renders for
    // it land in a detached node and show nothing -- the same outcome as
    // display mode. Grouping only, deliberately: resolving here too would be a
    // second answer to a question the extension has already answered, and the
    // two disagreeing is what leaves a placed marker permanently blank.
    setCitationHosts(
      citationGroups(citations ?? []).map((group) => ({
        key: group.key,
        host: hostFor(group.key),
        ids: group.ids,
      })),
    );
    setCitationNumbers(
      citationDecorationPluginKey.getState(editor.state)?.numbers ?? new Map(),
    );
  }, [editor, citations, citationsRenderable]);

  // Update search highlight decorations when searchQuery or activeMatchIndex changes
  useEffect(() => {
    if (!editor) return;

    editor.view.dispatch(
      editor.state.tr.setMeta(searchHighlightPluginKey, {
        searchQuery: searchQuery ?? "",
        activeMatchIndex: activeMatchIndex ?? -1,
      }),
    );
  }, [editor, searchQuery, activeMatchIndex]);

  // Handlers for suggestion accept/reject
  const handleSuggestionAccept = useCallback(
    (suggestionId: string) => {
      onSuggestionAccept?.(suggestionId);
    },
    [onSuggestionAccept],
  );

  const handleSuggestionReject = useCallback(
    (suggestionId: string) => {
      onSuggestionReject?.(suggestionId);
    },
    [onSuggestionReject],
  );

  return (
    <div
      className={`indigo-tiptap-editor${isDiffMode ? " indigo-tiptap-editor--diff-mode" : ""} ${className ?? ""}`}
    >
      {/* Toolbar: floating (BubbleMenu), static (always visible), or none */}
      {!isDiffMode && toolbarVariant === "floating" && (
        <FloatingToolbar editor={editor} />
      )}
      {!isDiffMode && toolbarVariant === "static" && (
        <StaticToolbar editor={editor} />
      )}
      {/* Show suggestion bubble menu when cursor is in a suggestion highlight */}
      {!isDiffMode && hasSuggestions && (
        <SuggestionBubbleMenu
          editor={editor}
          onAccept={handleSuggestionAccept}
          onReject={handleSuggestionReject}
          onNavigateToSegment={onNavigateToSegment}
        />
      )}
      <EditorContent editor={editor} />
      {citationsRenderable &&
        onOpenCitation &&
        onRemoveCitation &&
        citationHosts.map(({ key, host, ids }) =>
          createPortal(
            <CitationMarker
              actions={ids.map((id) => ({
                citationRowId: id,
                displayNumber: citationNumbers.get(id) ?? 0,
              }))}
              onOpen={onOpenCitation}
              onRemove={onRemoveCitation}
            />,
            host,
            key,
          ),
        )}
    </div>
  );
};
